import { Badge, Box, Checkbox, IconButtonWithTooltip, Td, Text, Tooltip, Tr } from '@elkaso-app/web-design';
import { mdiCheck, mdiPauseCircleOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { PriceImportRow } from 'apis/price-imports';
import { RematchPanel } from 'pages/price-imports/components/rematch-panel';
import { RowPriceCell } from 'pages/price-imports/components/row-price-cell';
import { tierPresentation } from 'pages/price-imports/utils/match-tier';
import { formatDeltaPct, priceDelta } from 'pages/price-imports/utils/price-delta';
import { bucketOf, BUCKET_COLOR_SCHEME, BUCKET_LABEL } from 'pages/price-imports/utils/row-buckets';
import type { PriceTier } from 'utils/price-units';
import { formatMoney, UomLadder } from 'utils/price-units';

export interface ImportRowLineProps {
  row: PriceImportRow;
  readOnly: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: (rowId: number) => void;
  onToggleExpand: (rowId: number) => void;
  onPriceCommit: (rowId: number, next: { price: number; tier: PriceTier | null }) => void;
  onSetStatus: (rowId: number, status: 'approved' | 'skipped' | 'needs_review') => void;
  onPickMaster: (rowId: number, masterProductId: number) => void;
}

/** The ladder to convert on: the row's own packaging is what a proposed variant would be created with. */
const ladderOf = (row: PriceImportRow): UomLadder => ({
  baseUnit: row.canonical.base_unit ?? null,
  itemUnit: row.canonical.item_unit ?? null,
  packUnit: row.canonical.pack_unit ?? null,
  netContent: row.canonical.net_content ? Number(row.canonical.net_content) : null,
  packSize: row.canonical.pack_size ? Number(row.canonical.pack_size) : null,
});

const variantLabel = (row: PriceImportRow): string =>
  [row.canonical.brand, row.canonical.origin, row.canonical.state].filter(Boolean).join(' · ');

export const ImportRowLine = ({
  row,
  readOnly,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  onPriceCommit,
  onSetStatus,
  onPickMaster,
}: ImportRowLineProps) => {
  const tier = tierPresentation(row.matchTier);
  const bucket = bucketOf(row.status);
  const blocking = (row.issues ?? []).filter((issue) => issue.severity === 'error');

  const delta = priceDelta(row.dealPrice, row.dealUnit, row.currentDealPrice, row.currentDealUnit, ladderOf(row));

  return (
    <>
      <Tr _hover={{ bg: 'gray.50' }} bg={isSelected ? 'red.50' : undefined}>
        <Td width='36px'>
          <Checkbox
            isChecked={isSelected}
            isDisabled={readOnly}
            onChange={() => onToggleSelect(row.id)}
            aria-label={`select-row-${row.rowNumber}`}
          />
        </Td>

        <Td>
          <Text fontSize='sm' noOfLines={2} title={row.canonical.source_name ?? ''}>
            {row.canonical.source_name ?? '—'}
          </Text>
          <Text fontSize='xs' color='gray.500'>
            Row {row.rowNumber}
            {row.canonical.supplier_code ? ` · ${row.canonical.supplier_code}` : ''}
          </Text>
        </Td>

        <Td>
          <Box
            as={readOnly ? undefined : 'button'}
            type={readOnly ? undefined : 'button'}
            textAlign='left'
            width='100%'
            onClick={readOnly ? undefined : () => onToggleExpand(row.id)}
            cursor={readOnly ? 'default' : 'pointer'}>
            {row.masterProductId ? (
              <>
                <Text fontSize='sm' fontWeight='medium' noOfLines={1}>
                  {row.canonical.universal_name ?? `#${row.masterProductId}`}
                </Text>
                <Text fontSize='xs' color='gray.500' noOfLines={1}>
                  {variantLabel(row) || '—'}
                </Text>
              </>
            ) : (
              <Text fontSize='sm' color='gray.500'>
                {row.proposal?.family ? `New family: ${row.proposal.family.universalName}` : 'Not matched'}
              </Text>
            )}
            {!readOnly && (
              <Text fontSize='10.5px' color='red.500'>
                {isExpanded ? 'Close' : 'Change product'}
              </Text>
            )}
          </Box>
        </Td>

        <Td>
          <Tooltip label={tier.hint} hasArrow>
            <Badge colorScheme={tier.colorScheme} variant='subtle'>
              {tier.label}
            </Badge>
          </Tooltip>
        </Td>

        <Td>
          <RowPriceCell
            price={row.dealPrice}
            tier={row.dealUnit}
            ladder={ladderOf(row)}
            sourceWord={row.priceUnitWord}
            scale={row.priceScale}
            readOnly={readOnly}
            onCommit={(next) => onPriceCommit(row.id, next)}
          />
        </Td>

        <Td>
          {delta.comparable && delta.pct != null ? (
            <Badge colorScheme={delta.pct > 0 ? 'red' : delta.pct < 0 ? 'green' : 'gray'} variant='subtle'>
              {formatDeltaPct(delta.pct)}
            </Badge>
          ) : (
            <Tooltip
              label={
                delta.reason === 'no-current'
                  ? 'This supplier has no price for this product yet.'
                  : delta.reason === 'different-unit'
                  ? 'The units cannot be compared, so no change is shown.'
                  : 'Not comparable.'
              }
              hasArrow>
              <Text fontSize='sm' color='gray.400'>
                —
              </Text>
            </Tooltip>
          )}
          {row.currentDealPrice != null && (
            <Text fontSize='xs' color='gray.500' whiteSpace='nowrap'>
              was {formatMoney(row.currentDealPrice)}
            </Text>
          )}
        </Td>

        <Td>
          <Badge colorScheme={BUCKET_COLOR_SCHEME[bucket]} variant='subtle'>
            {BUCKET_LABEL[bucket]}
          </Badge>
          {blocking.length > 0 && (
            <Box mt='4px'>
              <Text fontSize='xs' color='orange.700' noOfLines={2} title={blocking[0].message}>
                {blocking[0].message}
              </Text>
            </Box>
          )}
        </Td>

        <Td width='84px'>
          {!readOnly && (
            <Box display='flex' gap='2px'>
              <IconButtonWithTooltip
                size='xs'
                variant='ghost'
                label={bucket === 'ready' ? 'Send back for review' : 'Approve this row'}
                aria-label={`approve-row-${row.rowNumber}`}
                Icon={<Icon path={mdiCheck} size={0.7} />}
                onClick={() => onSetStatus(row.id, bucket === 'ready' ? 'needs_review' : 'approved')}
              />
              <IconButtonWithTooltip
                size='xs'
                variant='ghost'
                label={bucket === 'hold' ? 'Put back in review' : 'Hold this row, it will not be imported'}
                aria-label={`hold-row-${row.rowNumber}`}
                Icon={<Icon path={mdiPauseCircleOutline} size={0.7} />}
                onClick={() => onSetStatus(row.id, bucket === 'hold' ? 'needs_review' : 'skipped')}
              />
            </Box>
          )}
        </Td>
      </Tr>

      {isExpanded && !readOnly && (
        <Tr>
          <Td colSpan={8} p={0}>
            <RematchPanel row={row} onPickMaster={(masterProductId) => onPickMaster(row.id, masterProductId)} />
          </Td>
        </Tr>
      )}
    </>
  );
};
